import {
  type EnterpriseMemberDetailResponse,
  type EnterpriseMemberRowResponse,
  type EnterpriseMembersCountResponse,
  type EnterpriseMembersTableResponse,
  type GetMembersTableSortByEnum,
  type GetMembersTableSortDirEnum,
  type GetMembersTableStatusEnum,
  GetMembersTableWordsWindowEnum,
  type TeamListResponse,
} from './generated'
import { fetchAPI } from '.'

export type {
  EnterpriseMemberDetailResponse,
  EnterpriseMemberRowResponse,
  EnterpriseMembersCountResponse,
  EnterpriseMembersTableResponse,
  GetMembersTableSortByEnum,
  GetMembersTableSortDirEnum,
  GetMembersTableStatusEnum,
  TeamListResponse,
}
// ``GetMembersTableWordsWindowEnum`` is both a const object (runtime) and a type;
// re-export as a plain ``export`` so consumers can use it for either.
export { GetMembersTableWordsWindowEnum }

// Mirrors the OpenAPI ``GET /api/v1/enterprise/members/table`` query shape.
// ``search`` is trimmed and dropped when empty so it never serializes as
// ``?search=`` (backend treats present-but-empty as a no-op too, but emitting
// nothing keeps the cache key minimal).
export interface GetTeamMembersTableParams {
  search?: string
  status?: GetMembersTableStatusEnum
  teamId?: string
  sortBy?: GetMembersTableSortByEnum
  sortDir?: GetMembersTableSortDirEnum
  wordsWindow?: GetMembersTableWordsWindowEnum
  limit?: number
  offset?: number
  signal?: AbortSignal
}

export async function getTeamMembersTable({
  search,
  status,
  teamId,
  sortBy,
  sortDir,
  wordsWindow,
  limit,
  offset,
  signal,
}: GetTeamMembersTableParams = {}) {
  const trimmed = search?.trim()
  const params: Record<string, string | number> = {}
  if (trimmed) params.search = trimmed
  if (status) params.status = status
  if (teamId) params.team_id = teamId
  if (sortBy) params.sort_by = sortBy
  if (sortDir) params.sort_dir = sortDir
  if (wordsWindow) params.words_window = wordsWindow
  if (limit !== undefined) params.limit = limit
  if (offset !== undefined) params.offset = offset

  return fetchAPI<EnterpriseMembersTableResponse>({
    method: 'GET',
    url: '/members/table',
    params,
    signal,
  })
}

export async function getEnterpriseMembersCount(signal?: AbortSignal) {
  return fetchAPI<EnterpriseMembersCountResponse>({
    method: 'GET',
    url: '/members/count',
    signal,
  })
}

// Detail-page payload — same row shape as ``/members/table``. Chart cards on
// the detail page fetch filter-aware totals from ``/insights/words-dictated``
// and ``/insights/desktop-apps`` separately; this call has no query params.
export interface GetMemberDetailParams {
  userId: string
  signal?: AbortSignal
}

export async function getMemberDetail({ userId, signal }: GetMemberDetailParams) {
  return fetchAPI<EnterpriseMemberDetailResponse>({
    method: 'GET',
    url: `/members/${encodeURIComponent(userId)}`,
    signal,
  })
}

export async function getEnterpriseTeams(enterpriseId: string, signal?: AbortSignal) {
  return fetchAPI<TeamListResponse>({
    method: 'GET',
    url: `/${enterpriseId}/teams`,
    signal,
  })
}
